import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';

/**
 * useUnsavedChangesGuard({ when, title, message, confirmLabel, cancelLabel })
 *   → { dialog, allowNextNavigation }
 *
 * THE "leave without saving?" guard (2026-09-15, R14). One hook, every page
 * where typed work can be lost by leaving it. Render `dialog` anywhere in the
 * page; pass the page's ONE dirty predicate as `when`.
 *
 * Owner, 2026-09-15: *"when I start filling out an order and switch to a
 * differnt page the order is lost. SHould we have a popup warning saying order
 * is not saved, stay or leave and discard changes?"* — offered a guard on the
 * app's own links only, or a move to the data router so every way out is
 * caught, he chose *"Switch the app to the newer router setup, build it once
 * as a shared piece and use it everywhere work can be lost"*.
 *
 * ── WHAT IT CATCHES ───────────────────────────────────────────────────────
 *  • every react-router navigation while `when` is true — sidebar, header,
 *    bottom nav, in-page `<Link>`s, `navigate()` calls and the browser's
 *    Back/Forward (a POP) — via `useBlocker`, with an in-app `ConfirmDialog`:
 *    **Stay** (`blocker.reset()`, takes initial focus, Escape/overlay map to it)
 *    and **Leave and discard** (`blocker.proceed()`, danger);
 *  • a reload, tab close or a full-document navigation (`window.location`) —
 *    via `beforeunload`, which only ever shows the browser's GENERIC prompt:
 *    custom text there is impossible in every current browser.
 *
 * ── WHAT IT DELIBERATELY DOES NOT PROMPT ON ────────────────────────────────
 *  • A navigation that changes only `search`/`hash` (`shouldBlockLeaving`).
 *    The route element does not remount, so the page's state — the thing being
 *    protected — survives: Department Orders' queue tabs and deep-linked
 *    expanded rows, `ui/Tabs`' hash, `?upload=1`. Prompting there would train
 *    people to click "Leave" on a dialog that protects nothing.
 *  • A navigation the page itself makes after a SUCCESSFUL save — call
 *    `allowNextNavigation()` immediately before `navigate()`. It is a REF, not
 *    state, and that is the point: `setDirty(false); navigate(x)` in one
 *    handler reaches the blocker before React has re-rendered, so `when` is
 *    still `true` there and a state-based bypass would prompt the user to
 *    discard work that was just saved. The bypass is consumed by the next
 *    navigation attempt, cleared on the next microtask if none came (so it can
 *    never waive a LATER user navigation), and cleared whenever the guard re-arms.
 *
 * ── ONE BLOCKER PER ROUTER, STRUCTURALLY ───────────────────────────────────
 * react-router keeps a single active blocker: a second registration logs
 * "A router only supports one blocker at a time" and only the LAST one is
 * asked. So a page owns ONE guard over the union of its predicates (Budget:
 * Gantt strip OR member editor; Breakdown: table row OR timeline panel) and
 * picks the wording inside it — never a guard per row or per panel. The
 * blocker is mounted ONLY while armed, so an idle guard registers nothing.
 * `useUnsavedChangesGuard.test.js` §7 fails if two armed guards co-exist.
 *
 * ── OUTSIDE A DATA ROUTER IT DEGRADES TO `beforeunload` ONLY ───────────────
 * `useBlocker` throws outside `RouterProvider`. ~143 client test files render
 * pages under a plain `<MemoryRouter>` and the Budget suites render with NO
 * router at all, so the hook reads `UNSAFE_DataRouterContext` and mounts no
 * blocker when it is absent. 🚨 That makes the no-op a SILENT failure mode in
 * production if `App.js` ever went back to `<BrowserRouter>` — every guard
 * would still arm `beforeunload` and look alive. `App.dataRouter.test.js`
 * mounts the REAL `<App/>` and drives a guarded navigation through it; that
 * test, not this comment, is what keeps the no-op off the production path.
 *
 * ── HISTORY ────────────────────────────────────────────────────────────────
 * Replaced six hand-rolled `beforeunload` effects (OrderPage, OrderInlineEditor,
 * Grading, BudgetCostCodes, RiskAssessmentDetail, RiskAssessmentBlockEditor),
 * each of whose docblocks conceded it could not see an in-app navigation, and
 * `BudgetCostCodes.ganttDayExitGuard.test.js` N4 (*"the single biggest
 * remaining hole"*). Never add a second `beforeunload` beside this hook.
 */

export const UNSAVED_CHANGES_TITLE = 'Leave without saving?';
export const UNSAVED_CHANGES_LEAVE_LABEL = 'Leave and discard';
export const UNSAVED_CHANGES_STAY_LABEL = 'Stay';
export const UNSAVED_CHANGES_MESSAGE = "Your changes haven't been saved. If you leave, they will be lost.";
/** The three order-list pages' inline editor (one dirty row at a time). */
export const UNSAVED_ORDER_CHANGES_MESSAGE = "Your changes to this order haven't been saved. If you leave, they will be lost.";

const NO_ROUTER_CONTEXT = React.createContext(null);

/** Only a PATHNAME change leaves the page; search/hash-only changes keep its state. */
export function shouldBlockLeaving({ currentLocation, nextLocation }) {
  return !!currentLocation && !!nextLocation && currentLocation.pathname !== nextLocation.pathname;
}

// How many guards currently hold a router blocker. Read by tests (a page must
// never reach 2) and checked here so a second one is LOUD in development rather
// than the router's quiet "only the last blocker is asked".
let mountedBlockers = 0;
export function getMountedNavigationBlockerCount() {
  return mountedBlockers;
}
export const TWO_GUARDS_ERROR =
  'useUnsavedChangesGuard: two navigation guards are armed at once. react-router asks only the last one, '
  + 'so the other page state can be lost without a prompt. Give the page ONE guard over the union of its dirty predicates.';

function NavigationBlockerDialog({ shouldBlock, title, message, confirmLabel, cancelLabel }) {
  useEffect(() => {
    mountedBlockers += 1;
    if (mountedBlockers > 1 && process.env.NODE_ENV !== 'production') {
      console.error(TWO_GUARDS_ERROR);
    }
    return () => { mountedBlockers -= 1; };
  }, []);
  const blocker = useBlocker(shouldBlock);
  const blocked = blocker.state === 'blocked';
  return (
    <ConfirmDialog
      open={blocked}
      title={title}
      message={message}
      variant="danger"
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      initialFocus="cancel"
      onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed(); }}
      onCancel={() => { if (blocker.state === 'blocked') blocker.reset(); }}
    />
  );
}

export default function useUnsavedChangesGuard({
  when,
  title = UNSAVED_CHANGES_TITLE,
  message = UNSAVED_CHANGES_MESSAGE,
  confirmLabel = UNSAVED_CHANGES_LEAVE_LABEL,
  cancelLabel = UNSAVED_CHANGES_STAY_LABEL,
} = {}) {
  const armed = !!when;
  const armedRef = useRef(armed);
  armedRef.current = armed;
  const bypassRef = useRef(false);

  // A new edit starts with no stale bypass left over from an earlier save.
  useEffect(() => {
    if (armed) bypassRef.current = false;
  }, [armed]);

  const shouldBlock = useCallback((args) => {
    if (bypassRef.current) {
      bypassRef.current = false;
      return false;
    }
    return armedRef.current && shouldBlockLeaving(args);
  }, []);

  // 🔑 ONE-SHOT, AND ONLY FOR THE CURRENT TICK (R14 review M1). A bypass that
  // lived until "the next navigation attempt" leaked: called with no navigate()
  // after it (a save handler that bails), the user's NEXT sidebar click skipped
  // the prompt. react-router consults the blocker SYNCHRONOUSLY inside a push/
  // replace `navigate()` — before its first await — so clearing on a microtask
  // still covers `allowNextNavigation(); navigate(to)` in one handler. ⚠️ It does
  // NOT cover `navigate(-1)`: a POP reaches the blocker from a later `popstate`
  // event. No caller bypasses a POP; do not start one.
  const allowNextNavigation = useCallback(() => {
    bypassRef.current = true;
    Promise.resolve().then(() => { bypassRef.current = false; });
  }, []);

  useEffect(() => {
    if (!armed) return undefined;
    const handler = (e) => {
      if (bypassRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [armed]);

  // `|| NO_ROUTER_CONTEXT`: ~19 Budget/RA suites `jest.mock('react-router-dom')`
  // with a hand-written factory that has no `UNSAFE_DataRouterContext` at all,
  // and `useContext(undefined)` throws. Such a module is not a data router.
  const hasDataRouter = useContext(UNSAFE_DataRouterContext || NO_ROUTER_CONTEXT) != null;
  const dialog = hasDataRouter && armed ? (
    <NavigationBlockerDialog
      shouldBlock={shouldBlock}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
    />
  ) : null;

  return { dialog, allowNextNavigation };
}
